import { Injectable, OnDestroy } from '@angular/core';

import { decodeJwt, JWTPayload } from 'jose';
import { NgEventBus } from 'ng-event-bus';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { Device } from '../../models/auth.model';
import { ViewContent } from '../../models/view-content.models/view-content.model';
import { ViewContentCacheService } from '../cache/view-content-cache.service';
import { DeviceFacadeService } from '../facades/device-facade.service';
import { ServerSentEventService } from './server-sent-event.service';

@Injectable({
  providedIn: 'root',
})
export class ServerSentEventHandlerService implements OnDestroy {
  private currentDevice: Device | null = null;
  private activeTopics = new Set<string>();
  private allSubs: Subscription[] = [];

  private isSubscribed: boolean = false;

  private processedCount: number = 0;

  private locatorStatusSubject = new BehaviorSubject<{
    remaining: number;
    processed: number;
    locatorLog: { locator: string; success: boolean }[];
  }>({
    remaining: 0,
    processed: this.processedCount,
    locatorLog: [],
  });

  public locatorStatus$: Observable<{
    remaining: number;
    processed: number;
    locatorLog: { locator: string; success: boolean }[];
  }> = this.locatorStatusSubject.asObservable();

  constructor(
    private readonly eventBus: NgEventBus,
    private readonly deviceFacade: DeviceFacadeService,
    private readonly sseService: ServerSentEventService,
    private vcCache: ViewContentCacheService,
  ) {
    this.initializeViewContentCacheSubscriptions();
  }

  public initializeMercureTopicSubscriptions(accessToken: string): void {
    if (this.isSubscribed) {
      console.warn('Already subscribed. Skipping initialization.');
      return;
    }

    if (!this.isValidToken(accessToken)) {
      console.error('Invalid access token provided.');
      return;
    }

    this.isSubscribed = true;

    this.allSubs.push(
      this.deviceFacade.device$.subscribe({
        next: deviceState => this.handleDeviceChange(deviceState),
        error: error => this.handleError(error, 'deviceSubscription'),
      }),
    );

    this.allSubs.push(
      this.deviceFacade.getDeviceId().subscribe({
        next: (deviceId: string) => {
          this.handleDeviceIdChange(deviceId);
        },
        error: error => this.handleError(error, 'deviceIdSubscription'),
      }),
    );

    this.subscribeToServerSentEvents(['periodicPing'], (eventData: any) => {
      this.eventBus.cast('sse:periodicPing', eventData);
    });
  }

  public ngOnDestroy(): void {
    this.allSubs.forEach(sub => sub.unsubscribe());
    this.activeTopics.clear();
    this.clearSensitiveData();
  }

  private initializeViewContentCacheSubscriptions(): void {
    const locatorSub = this.vcCache.incomingLocator$.subscribe({
      next: (locator: string) => {
        this.handleIncomingLocator(locator);
      },
      error: error => this.handleError(error, 'incomingLocator$ Subscription'),
    });

    const viewContentSub = this.vcCache.incomingViewContents$.subscribe({
      next: viewContent => {
        this.handleIncomingViewContent(viewContent);
      },
      error: error => this.handleError(error, 'incomingViewContents$ Subscription'),
    });

    this.allSubs.push(locatorSub, viewContentSub);
  }

  private handleIncomingLocator(locator: string): void {
    if (locator.startsWith('case.list')) return;

    const currentStatus = this.locatorStatusSubject.getValue();
    const updatedRemaining = currentStatus.remaining + 1;
    const updatedLog = [...currentStatus.locatorLog, { locator, success: false }];

    this.locatorStatusSubject.next({
      remaining: updatedRemaining,
      processed: currentStatus.processed,
      locatorLog: updatedLog,
    });
  }

  private handleIncomingViewContent(viewContent: ViewContent<any>): void {
    const currentStatus = this.locatorStatusSubject.getValue();

    const locator = (viewContent as any).locator as string;

    const logIndex = currentStatus.locatorLog.findIndex(
      log => log.locator === locator && !log.success,
    );

    if (logIndex !== -1) {
      const updatedLog = [...currentStatus.locatorLog];
      updatedLog[logIndex] = { locator, success: true };

      this.locatorStatusSubject.next({
        remaining: currentStatus.remaining - 1,
        processed: currentStatus.processed + 1,
        locatorLog: updatedLog,
      });
    } else {
      console.warn(`Kein passender Locator für ViewContent gefunden: ${locator}`);
    }
  }

  private handleDeviceChange(deviceState: { device: Device | null }): void {
    const deviceId = deviceState.device?.deviceId;
    if (deviceId && !this.activeTopics.has(`device/${deviceId}`)) {
      this.currentDevice = { ...this.currentDevice, deviceId };
      this.subscribeToServerSentEvents([`device/${deviceId}`], (eventData: any) => {
        this.eventBus.cast(`sse:device/${deviceId}`, eventData);
      });
    }
  }

  private handleDeviceIdChange(deviceId: string): void {
    if (deviceId && !this.activeTopics.has(`sse:device/${deviceId}`)) {
      this.allSubs.push(
        this.eventBus.on(`sse:device/${deviceId}`).subscribe({
          error: error => this.handleError(error, 'eventBusSubscription'),
        }),
      );
    }
  }

  private subscribeToServerSentEvents(topics: string[], onNext: (eventData: any) => void): void {
    topics.forEach(topic => {
      if (!this.activeTopics.has(topic)) {
        this.activeTopics.add(topic);
        this.allSubs.push(
          this.sseService.getServerSentEvents(topic).subscribe({
            next: onNext,
            error: error => this.handleError(error, `${topic} event`),
          }),
        );
      }
    });
  }

  private handleError(error: any, context: string): void {
    console.error(`Error in ${context}:`, error);
  }

  private clearSensitiveData(): void {
    this.currentDevice = null;
  }

  private isValidToken(token: string): boolean {
    if (!token) return false;

    const parts = token.split('.');
    if (parts.length !== 3) return false;

    try {
      const decoded = decodeJwt(token) as JWTPayload;
      if (decoded.exp && Date.now() >= decoded.exp * 1000) return false;
    } catch (error) {
      return false;
    }

    return true;
  }
}
